import { createContext, useContext, ReactNode } from "react"
import { useQuery } from "@tanstack/react-query"
import { fetchDonationsSummary } from "../queries"
import { AlertMessage, Loading } from "source/shared/components"
import { DonationsSummary } from "../types"

type DonationsSummaryContextType = {
  donationsSummary: DonationsSummary
}

const DonationsSummaryContext = createContext<
  DonationsSummaryContextType | undefined
>(undefined)

export const DonationsSummaryProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const {
    data: { donationsSummary } = {},
    isLoading: isLoadingDonationsSummary,
    isError: isErrorDonationsSummary,
  } = useQuery({
    queryKey: ["DonationsSummary"],
    queryFn: fetchDonationsSummary,
  })

  if (isErrorDonationsSummary) {
    return (
      <AlertMessage type="error" icon="general#exclamation-triangle">
        Something went wrong. Please try again.
      </AlertMessage>
    )
  }

  if (isLoadingDonationsSummary || !donationsSummary) {
    return <Loading message="Loading" />
  }

  return (
    <DonationsSummaryContext.Provider
      value={{
        donationsSummary,
      }}
    >
      {children}
    </DonationsSummaryContext.Provider>
  )
}

export const useDonationsSummaryContext = () => {
  const context = useContext(DonationsSummaryContext)
  if (!context) {
    throw new Error(
      "useDonationsSummaryContext must be used within a DonationsSummaryProvider",
    )
  }
  return context
}
